import { Injectable, EventEmitter } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { TableDetails } from './table-details.model';
import { SearchParams } from './search-params.model';
import { Pager } from './pager.model';
import { Column } from './column.model';

@Injectable({
  providedIn: 'root',
})
export class TableFilterService {
  private searchInputSub!: Subscription;
  private tableActionSub!: Subscription;
  private rowClickActionSub!: Subscription;

  private pager: any;
  private searchParams: any;

  // Defined the observables which are required to run the table & filter components.
  private tableDetailsSource = new BehaviorSubject<TableDetails>({
    tableId: '',
    allowRowClick: false,
    rowClickURI: '',
    tableTitle: '',
    tableData: [],
    sendDefault90DayFilter: false,
    defaultItemPerPage: 10,
  });
  public tableDetails = this.tableDetailsSource.asObservable();

  private searchInputSource = new BehaviorSubject<{ searchParams: SearchParams; pager: Pager }>(
    this.createInitialSearchInput(null)
  );
  public searchInput = this.searchInputSource.asObservable();

  private tableFilterColumnsSource = new BehaviorSubject<Column[]>([]);
  public tableFilterColumns = this.tableFilterColumnsSource.asObservable();

  // An emmitter to trigger the clearning of filters.
  public clearFiltersEvent = new EventEmitter<void>();

  // Emitters to send back action triggers to the page component.
  public tableActionEvent = new EventEmitter<any>();

  // emitter to send back trigger for alternate action on row click.
  public rowClickCallbackEvent = new EventEmitter<any>();

  // This
  init(columns: Column[], tableDetails: TableDetails) {
    const currentTableDetails = this.tableDetailsSource.value;

    //  check to see if any observables have been used by different components and resets as necessary.
    this.clearSubscriptions();

    // If this table is already initialized, don't reset everything.
    if (tableDetails.tableId == currentTableDetails.tableId) {
      return;
    }

    // init the table data container.
    tableDetails.tableData = [];

    // Set the table details which contains a holder for
    this.setTableDetails(tableDetails);

    // Run through each column and set up default values necessary for filter behavior.
    for (const column of columns) {
      if (column.display == 'concealed') {
        column.revealedContent = [];
      }

      if (column.filter) {
        column.filter.visible = false;
        column.filter.active = false;
        if (column.filter.type == 'string') {
          column.filter.searchString = '';
        }
        if (column.filter.type == 'multipleChoice' && column.filter.options) {
          column.filter.selected = [];
          for (const _option of column.filter.options) {
            column.filter.selected.push(false);
          }
        } else if (column.filter.type == 'date') {
          column.filter.dateRange = { from: null, to: null };
        }
      }
    }

    // Set the column and filter data to be used across both components.
    this.setTableFilterColumns(columns);

    // Reset or intialize the pager and searchParams
    this.resetSearchInput(tableDetails.defaultItemPerPage);
  }

  // Register subscriptions as they are created at the page level. This allows us to clear them since the app is not destroying components on route change.
  registerSubscriptions(
    searchInputSub: Subscription,
    tableActionSub: Subscription,
    rowClickActionSub?: Subscription
  ) {
    this.searchInputSub = searchInputSub;
    this.tableActionSub = tableActionSub;
    if (rowClickActionSub) {
      this.rowClickActionSub = rowClickActionSub;
    }
  }

  // Clear subscriptions if they have been created by previous pages that utilized the table filters.
  clearSubscriptions(): void {
    if (this.searchInputSub) {
      this.searchInputSub.unsubscribe();
      this.searchInputSub = new Subscription();
    }

    if (this.tableActionSub) {
      this.tableActionSub.unsubscribe();
      this.tableActionSub = new Subscription();
    }

    if (this.rowClickActionSub) {
      this.rowClickActionSub.unsubscribe();
      this.rowClickActionSub = new Subscription();
    }
  }

  // Initialize up the searchParams (used for sending to API call methods) and pager (used for pagination)
  createInitialSearchInput(defaultItemsPerPage: number | null) {
    const itemsPerPage: number = defaultItemsPerPage ? defaultItemsPerPage : 10;
    this.pager = this.initiatePager(itemsPerPage);
    this.searchParams = {};
    return this.buildSearchInputObject();
  }

  // Assembles searchInput data set, mostly used when updating observables.
  buildSearchInputObject() {
    const tableDetails = this.tableDetailsSource.value;

    const searchInputObject = {
      pager: this.pager,
      searchParams: this.searchParams,
    };

    // If the able is configured to send a date filter by default, and there are no active filters, add it here.
    if (tableDetails.sendDefault90DayFilter && !this.checkTableHasActiveFilters())
      searchInputObject.searchParams = this.addDefault90DayFilter(searchInputObject.searchParams);

    return searchInputObject;
  }

  // Add a 90 day date filter using the configured parameters.
  addDefault90DayFilter(searchParams: any) {
    const tableDetails = this.tableDetailsSource.value;

    if (
      !tableDetails.default90DayFilterFromRequestKey ||
      !tableDetails.default90DayFilterToRequestKey
    ) {
      throw new Error(
        'Default keys are required to execute a default date filter. Set default90DayFilterFromRequestKey and default90DayFilterToRequestKey in the table details.'
      );
    }

    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 90);

    searchParams[tableDetails.default90DayFilterFromRequestKey] = this.formatDate(from);
    searchParams[tableDetails.default90DayFilterToRequestKey] = this.formatDate(to);
    return searchParams;
  }

  // Check to see if default date filters have been set, and if so, clear them.
  clearDefaultFilters() {
    const tableDetails = this.tableDetailsSource.value;

    // If this table is not set up for default date filters, we don't need to proceed.
    if (
      !tableDetails.sendDefault90DayFilter ||
      !tableDetails.default90DayFilterFromRequestKey ||
      !tableDetails.default90DayFilterToRequestKey
    )
      return;

    if (this.searchParams[tableDetails.default90DayFilterFromRequestKey])
      delete this.searchParams[tableDetails.default90DayFilterFromRequestKey];

    if (this.searchParams[tableDetails.default90DayFilterToRequestKey])
      delete this.searchParams[tableDetails.default90DayFilterToRequestKey];

    // Note: we don't rebuild here, as this is only called when we are adding a new filter, and the rebuild process will happen at the end of that process.
  }

  // format the date to YYYY-MM-DD
  formatDate(date: any) {
    if (!this.isDate(date)) return date;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  // Check if a variable is a Date object
  isDate(variable: any): boolean {
    return variable instanceof Date;
  }

  // Check to see if any fitler has been activated. Returns boolean.
  checkTableHasActiveFilters(): boolean {
    const columns = this.tableFilterColumnsSource.value;
    for (const column of columns) {
      if (column.filter && column.filter.active) return true;
    }
    return false;
  }

  // Check to see if the search has been activated. Returns boolean.
  checkTableHasActiveSearch(): boolean {
    if (this.searchParams['SearchText']) return true;

    return false;
  }

  // Returns the current active search string if any.
  getActiveSearchTerm(): string {
    if (this.searchParams['SearchText']) return this.searchParams['SearchText'];

    return '';
  }

  // Initializes or resets the SearchInput observable.
  resetSearchInput(defaultItemPerPage: number | null) {
    this.createInitialSearchInput(defaultItemPerPage);
    this.searchInputSource.next(this.buildSearchInputObject());
  }

  // Trigger to clear all active filters.
  clearFilters() {
    this.clearFiltersEvent.emit();
    this.searchParams = {};
    this.pager.currentPage = 1;
    this.searchInputSource.next(this.buildSearchInputObject());
  }

  prepForNewFilter() {
    delete this.searchParams.SearchText;
    this.pager.currentPage = 1;
  }

  prepForNewSearch() {
    this.searchParams = {};
    this.pager.currentPage = 1;
  }

  // Triggers the emitter to send back the selected action. This is subscribed at the page component level and should be configured there.
  triggerAction(data: any) {
    this.tableActionEvent.emit(data);
  }

  // Triggers the emitter to send back a callback event on a row click. (Only used when confiugred. Default behavior is router navigation.)
  triggerRowClickCallback() {
    this.rowClickCallbackEvent.emit();
  }

  // Clears a single filter and updates the observable.
  clearFilter(requestKey: string) {
    if (this.searchParams[requestKey]) delete this.searchParams[requestKey];

    this.searchInputSource.next(this.buildSearchInputObject());
  }

  // Clear a single date filter and update observable.
  clearDateFilters(requestKeyFrom: string, requestKeyTo: string) {
    if (this.searchParams[requestKeyFrom]) delete this.searchParams[requestKeyFrom];

    if (this.searchParams[requestKeyTo]) delete this.searchParams[requestKeyTo];

    this.searchInputSource.next(this.buildSearchInputObject());
  }

  clearAmountFilters(exactKey: string, minKey: string, maxKey: string) {
    if (this.searchParams[exactKey]) delete this.searchParams[exactKey];

    if (this.searchParams[minKey]) delete this.searchParams[minKey];

    if (this.searchParams[maxKey]) delete this.searchParams[maxKey];

    this.searchInputSource.next(this.buildSearchInputObject());
  }

  // Sets a filter as visible when interacting with the filter inputs.
  setFilterVisible(filterName: string) {
    const tableFiltercolumns = this.tableFilterColumnsSource.value;
    for (const column of tableFiltercolumns) {
      if (column.filter) column.filter.visible = column.name == filterName ? true : false;
    }

    this.tableFilterColumnsSource.next(tableFiltercolumns);
  }

  // Trigger the event when the sort is clicked in the table header on enabled columns. Toggles the status and updates the observable.
  updateSort(column: Column) {
    if (!column.isSortable) return;

    this.searchParams.Asc = column.sortState == 'asc' ? true : false;
    this.searchParams.SortField = column.requestKey;

    this.searchInputSource.next(this.buildSearchInputObject());
  }

  // Update the page selected when interacting with the pagination.
  updatePage(page: number) {
    this.pager.currentPage = page;
    this.searchInputSource.next(this.buildSearchInputObject());
  }

  // Triggered when the user submits any input on a filter or the search bar. Sets up the searchParams object which is sent to the API methods.
  addFilter(filterToAdd: any, column: Column | null) {
    if (filterToAdd.type === 'globalSearch') {
      this.searchParams[filterToAdd.key] = filterToAdd.value.trimStart();
    } else if (filterToAdd.type == 'string') {
      this.searchParams[filterToAdd.key] = filterToAdd.value;
    } else if (filterToAdd.type == 'email') {
      this.searchParams[filterToAdd.key] = filterToAdd.value;
    } else if (filterToAdd.type == 'amount') {
      if (filterToAdd.exact !== null) {
        this.searchParams[filterToAdd.exactKey] = filterToAdd.exactValue;
      }
      if (filterToAdd.min !== null) {
        this.searchParams[filterToAdd.minKey] = filterToAdd.minValue;
      }
      if (filterToAdd.max !== null) {
        this.searchParams[filterToAdd.maxKey] = filterToAdd.maxValue;
      }
    } else if (filterToAdd.type == 'multipleChoice') {
      const multipleChoiceValue = this.getMultipleChoiceValue(
        column?.filter?.options ?? [],
        column?.filter?.selected ?? []
      );
      // If the value is truthy, we add it to the searchParamData.
      if (multipleChoiceValue) {
        this.searchParams[filterToAdd.key] = multipleChoiceValue;
      } else {
        // Otherwise, check if this key already exists. If so, remove it.
        if (
          this.searchParams &&
          Object.prototype.hasOwnProperty.call(this.searchParams, filterToAdd.key)
        )
          delete this.searchParams[filterToAdd.key];
      }
    }
    this.pager.currentPage = 1;
    this.searchInputSource.next(this.buildSearchInputObject());
  }

  addDateFilters(filtersToAdd: any, _column: Column | null) {
    if (
      !filtersToAdd.fromKey ||
      !filtersToAdd.fromValue ||
      !filtersToAdd.toKey ||
      !filtersToAdd.toValue
    ) {
      throw new Error('Date values for filter not correct.');
    }

    this.searchParams[filtersToAdd.fromKey] = filtersToAdd.fromValue;
    this.searchParams[filtersToAdd.toKey] = filtersToAdd.toValue;
    this.pager.currentPage = 1;
    this.searchInputSource.next(this.buildSearchInputObject());
  }

  // Gets the current value of the all selected options for a multiple choice filter. Sends it back as a comma separated string.
  getMultipleChoiceValue(values: any[], flags: boolean[]): string | null {
    if (values.length !== flags.length) throw new Error('Arrays must be of the same length.');

    const filteredArray = [];
    for (let i = 0; i < values.length; i++) {
      if (flags[i]) {
        filteredArray.push(values[i].key);
      }
    }

    // If filteredArray has the same length as values, it means every selection has been checked. so we return null. This will remove the filter from the list.
    if (filteredArray.length == values.length) return null;

    return filteredArray.join(',');
  }

  // Updates the table details, usually called after the API call has completed and data to display has been updated.
  setTableDetails(tableDetails: TableDetails) {
    tableDetails.pager = this.pager;
    this.tableDetailsSource.next(tableDetails);
  }

  // Update the columns observable.
  setTableFilterColumns(tableFilterColumns: Column[]) {
    this.tableFilterColumnsSource.next(tableFilterColumns);
  }

  // Triggered when the user selects a different value for Items Per Page. Resets the pager and updates the observable.
  updateItemsPerPage(itemsPerPage: number) {
    this.pager = this.initiatePager(itemsPerPage);
    this.searchInputSource.next(this.buildSearchInputObject());
  }

  /*** Pager handling methods. These are ported over from the commonService so that this functionality can be self-contained. ***/
  initiatePager(resultsPerPage?: number) {
    const pager: Pager = {
      currentPage: 1,
      totalPages: 0,
      resultPerPage: resultsPerPage ? resultsPerPage : 10,
      totalResults: 0,
      pages: [],
      data: [],
    };
    return pager;
  }

  prepareSearchParamsForAPICall(pageNumber: number) {
    this.searchParams.PageSize = this.pager.resultPerPage;
    this.searchParams.StartRow = this.calculatePageStartRow(pageNumber, this.pager.resultPerPage);
    return this.searchParams;
  }

  setPager(result: any, pageNumber: number, pager: any) {
    const data = result.data;
    const dataCount = result.totalRowCount;
    const pageCount = Math.ceil(dataCount / pager.resultPerPage);
    if (dataCount > 0) {
      pager.totalPages = pageCount;
      pager.results = dataCount;
      pager.totalResults = dataCount;
      pager.data = data;
      pager.currentResults = data.length;
      pager.currentPage = pageNumber;
      pager.pages = this.getPaginationNumberArray(dataCount, pageNumber, pager.resultPerPage);
      pager.startRecord = pager.currentPage * pager.resultPerPage - (pager.resultPerPage - 1);
      pager.endRecord =
        pager.currentPage * pager.resultPerPage > pager.totalResults
          ? pager.totalResults
          : pager.currentPage * pager.resultPerPage;
    } else {
      pager = this.initiatePager();
    }
    return pager;
  }

  calculatePageStartRow(pageNumber: number, resultPerPage: number) {
    return (pageNumber * 1 - 1) * (resultPerPage * 1);
  }

  getPaginationNumberArray(totalItems: number, currentPage: number, pageSize: number) {
    const totalPages = Math.ceil(totalItems / pageSize);
    let startPage: number, endPage: number;

    if (totalPages <= 5) {
      startPage = 1;
      endPage = totalPages;
    } else {
      if (totalPages - currentPage < 2) {
        startPage = totalPages - 4;
      } else {
        startPage = currentPage - 2 <= 0 ? 1 : currentPage - 2;
      }
      if (currentPage <= 2) {
        endPage = 5;
      } else {
        endPage = currentPage + 2 >= totalPages ? totalPages : currentPage + 2;
      }
    }
    return this.rangeFunc(startPage, endPage + 1, false);
  }

  rangeFunc(start: any, stop: any, step: any) {
    if (stop == null) {
      stop = start || 0;
      start = 0;
    }
    if (!step) {
      step = stop < start ? -1 : 1;
    }

    const length = Math.max(Math.ceil((stop - start) / step), 0);
    const range = Array(length);

    for (let idx = 0; idx < length; idx++, start += step) {
      range[idx] = start;
    }

    return range;
  }
}
